"use client";

import { cn } from "@/lib/utils";

/** Ring colours per band, in order (Figma ellipses 10169:195…202). */
export const RING_COLORS = ["#e8c872", "#c9d3cf", "#d39a6a", "#0e5b44", "#1b8869", "#7ccb9e", "#a3deb2", "#cdebdb"];

/**
 * Figma ring: 520px box, 10.4px stroke on the outside edge, slices proportional to each band's
 * share of the pool, ~6px apart, clockwise from 12 o'clock. Drawn on a 520 viewBox so the mobile
 * 340px ring scales the stroke the same way.
 */
const SIZE = 520;
const STROKE = 10.4;
const R = (SIZE - STROKE) / 2;
const C = 2 * Math.PI * R;
const GAP = (6 / (2 * Math.PI * (SIZE / 2))) * C;

interface Props {
  /** Share of the pool per band (sums to 1). */
  fractions: number[];
  /** Which bands are filled. */
  filled: boolean[];
  /** Band that is "lit" right now; -1 when none (the resting, as-drawn state). */
  active: number;
  className?: string;
}

export default function PrizeRing({ fractions, filled, active, className }: Props) {
  let start = 0;
  const arcs = fractions.map((f) => {
    const arc = { offset: start * C + GAP / 2, length: Math.max(0, f * C - GAP) };
    start += f;
    return arc;
  });

  return (
    <svg aria-hidden="true" viewBox={`0 0 ${SIZE} ${SIZE}`} className={cn("overflow-visible", className)}>
      <g transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`} fill="none">
        {/* unfilled slices: a faint track in the same places, so the gaps stay white */}
        {arcs.map((a, i) => (
          <circle
            key={`t${i}`}
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={R}
            stroke="rgba(0,27,20,0.06)"
            strokeWidth={STROKE}
            strokeDasharray={`${a.length} ${C}`}
            strokeDashoffset={-a.offset}
          />
        ))}
        {arcs.map((a, i) => {
          const lit = i === active;
          const dimmed = active >= 0 && !lit;
          return (
            <circle
              key={`a${i}`}
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={R}
              stroke={RING_COLORS[i % RING_COLORS.length]}
              strokeWidth={lit ? STROKE * 1.6 : STROKE}
              strokeDasharray={`${filled[i] ? a.length : 0} ${C}`}
              strokeDashoffset={-a.offset}
              style={{
                opacity: dimmed ? 0.45 : 1,
                filter: lit ? `drop-shadow(0 0 10px ${RING_COLORS[i % RING_COLORS.length]})` : undefined,
                transition:
                  "stroke-dasharray 900ms cubic-bezier(0.22,0.61,0.36,1), stroke-width 400ms ease, opacity 400ms ease, filter 400ms ease",
              }}
            />
          );
        })}
      </g>
    </svg>
  );
}
